/**
 * Rig State targets and recalls in words (2026-10-06).
 *
 * One place for "what is this target called" and "what did that recall do",
 * so the stage button and the Rig States section say the same thing.
 *
 * A failed target is NAMED, with its reason: a look now travels between PCs
 * (S305), and a target on a port only one PC has (a Helix on the Mac) fails
 * on the other. "Recalled 3/4" told the operator something was wrong but not
 * what; this says which target, and why.
 */

import { LIVE_TARGETS } from '../liveState/registry.ts';
import { parseMidiTargetId } from '../liveState/resolve.ts';
import { DEFAULT_PORT_TOKEN } from '../liveState/sendCache.ts';
import type { RecallOutcome } from './store.ts';

/** "Transpose", "CC 20, channel 3", "CC 69, channel 1 (Helix)". The port is
 *  named only when it is not the computer's default output. */
export function targetLabel(targetId: string): string {
  const named = LIVE_TARGETS.find((t) => t.id === targetId)?.label;
  if (named) return named;
  const m = parseMidiTargetId(targetId);
  if (!m) return targetId;
  const what = m.kind === 'cc' ? `CC ${m.number}` : `Note ${m.number}`;
  return m.port === DEFAULT_PORT_TOKEN ? `${what}, channel ${m.channel}` : `${what}, channel ${m.channel} (${m.port})`;
}

/** A dispatch result code in words. A real error message (an HTTP error's
 *  text, a port the computer does not have) passes through as it is. */
const KIND_WORDS: Record<string, string> = {
  'no-bridges': 'no computer connected',
  'bridge-unknown': 'that computer is not set up here',
  'bridge-disconnected': 'that computer is not connected',
  'port-not-configured': 'no MIDI output chosen for that computer',
  'unsupported-step': 'this computer cannot send that',
  'bad-note': 'not a note this app knows',
  'app-not-linked': 'the app it goes to is not linked',
};
function reasonWords(detail: string): string { return KIND_WORDS[detail] ?? detail; }

/** The toast for a recall: all sent, nothing to send, or which target did
 *  not go and why (the first one, plus how many more). */
export function recallSummary(lookName: string, outcomes: RecallOutcome[]): { tone: 'ok' | 'warn' | 'error'; text: string } {
  const total = outcomes.length;
  if (total === 0) return { tone: 'warn', text: `"${lookName}" has no targets yet` };
  const failed = outcomes.filter((o) => !o.wrote);
  if (failed.length === 0) return { tone: 'ok', text: `Recalled "${lookName}" - ${total}/${total}` };
  const first = failed[0]!;
  const why = first.detail ? `: ${reasonWords(first.detail)}` : '';
  const more = failed.length > 1 ? ` (and ${failed.length - 1} more)` : '';
  return { tone: 'error', text: `"${lookName}" recalled ${total - failed.length}/${total}. Not sent: ${targetLabel(first.targetId)}${why}${more}` };
}
